import React from "react";
import { FormikProps } from "formik";

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import CalendarDateTime from "@components/CalendarDateTime/CalendarDateTime";
import QuillEditor from "@components/QuillEditor";

import {
  IFormHookValues,
  IFormHookActions,
  FormFieldEnum,
  richTextModules,
} from "./hook/interface";
import { INotificationFormValues } from "./schema/notifications.schema";

import { userProfileTypeList } from "@utils/enum/profile";
import { segmentsTypeList } from "@utils/enum/segment";
import { filialTypeList } from "@utils/enum/filial";
import { buildErrorMessages } from "./hook/helper";
import { actionsConfig } from "./hook/rules.actions";

interface Props {
  formikProps: FormikProps<INotificationFormValues>;
  formHookValues: IFormHookValues;
  formHookActions: IFormHookActions;
}

const {
  CENTERS,
  SEGMENTS,
  PROFILES,
  TITLE,
  CONTENT,
  START_AT,
  EXPIRE_AT,
} = FormFieldEnum;
const NotificationForm: React.FC<Props> = (props) => {
  const {
    formikProps: formik,
    formHookValues: { mode, isCreate, isView, loading },
    formHookActions: {
      handlePreview: onPreview,
      handleCancelOrClose: onCancelOrClose,
    },
  } = props;

  const { showPreview, showSubmit, submitLabel } = actionsConfig[mode];

  const disabled = isView || loading;

  const textFieldSx = isView
    ? {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#eae9e9ff",
          "& fieldset": { borderColor: "transparent" },
        },
      }
    : {
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#FFFFFF",
          borderRadius: 1,
        },
      };

  const error = (name: keyof INotificationFormValues) => {
    const hasError = !!formik.errors[name];
    if (!hasError) return undefined;

    const isTouched = formik.touched[name];
    const hasSubmitted = formik.submitCount > 0;

    if (isTouched || hasSubmitted) {
      return formik.errors[name];
    }

    return undefined;
  };

  const handleMultiSelectChange = (
    field: typeof CENTERS | typeof SEGMENTS | typeof PROFILES
  ) => (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    formik.setFieldValue(
      field,
      typeof value === "string" ? value.split(",") : (value as string[])
    );
  };

  const renderMultiValueWithPlaceholder = (
    placeholder: string,
    list?: Array<{ value: string; label: string }>
  ) => (selected: unknown) => {
    const values = selected as string[];

    if (!values || values.length === 0) {
      return <span style={{ color: "#9E9E9E" }}>{placeholder}</span>;
    }

    if (list) {
      return values
        .map((val) => list.find((item) => item.value === val)?.label || val)
        .join(", ");
    }

    return values.join(", ");
  };

  return (
    <Box mt={2}>
      <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 2, p: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
              {isCreate
                ? "Informe os parâmetros criar uma nova notificação."
                : "Informe os parâmetros a serem alterados na notificação"}
            </Typography>
          </Grid>

          {/* Título */}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              Título
            </Typography>
            <TextField
              fullWidth
              disabled={disabled}
              placeholder={
                isCreate ? "Informe o título da notificação" : undefined
              }
              {...formik.getFieldProps(TITLE)}
              error={!!error(TITLE)}
              helperText={error(TITLE)}
              sx={textFieldSx}
            />
          </Grid>

          {/* Mensagem */}
          <Grid item xs={12}>
            <QuillEditor
              label="Mensagem"
              value={formik.values.content}
              onChange={(html) => formik.setFieldValue(CONTENT, html)}
              onBlur={() => formik.setFieldTouched(CONTENT, true)}
              placeholder={
                isCreate ? "Digite a mensagem da notificação" : undefined
              }
              isDisabled={disabled}
              hasError={!!error(CONTENT)}
              messageError={error(CONTENT) as string}
              height={250}
              modules={richTextModules}
            />
          </Grid>

          {/* Destinatários */}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: -3 }}>
              Destinatários
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              disabled={disabled}
              placeholder="Selecione um centro"
              value={formik.values.centers}
              onChange={handleMultiSelectChange(CENTERS)}
              helperText={error(CENTERS)}
              error={!!error(CENTERS)}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: renderMultiValueWithPlaceholder(
                  "Selecione um centro"
                ),
              }}
              sx={textFieldSx}
            >
              {filialTypeList.map((item) => {
                return <MenuItem value={item.value}>{item.label}</MenuItem>;
              })}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              disabled={disabled}
              placeholder="Selecione um segmento"
              value={formik.values.segments}
              onChange={handleMultiSelectChange(SEGMENTS)}
              helperText={error(SEGMENTS)}
              error={!!error(SEGMENTS)}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: renderMultiValueWithPlaceholder(
                  "Selecione um segmento"
                ),
              }}
              sx={textFieldSx}
            >
              {segmentsTypeList.map((item) => {
                return <MenuItem value={item.value}>{item.label}</MenuItem>;
              })}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              disabled={disabled}
              placeholder="Selecione um perfil"
              value={formik.values.profiles}
              onChange={handleMultiSelectChange(PROFILES)}
              helperText={error(PROFILES)}
              error={!!error(PROFILES)}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: renderMultiValueWithPlaceholder(
                  "Selecione um cliente",
                  userProfileTypeList
                ),
              }}
              sx={textFieldSx}
            >
              {userProfileTypeList.map((item) => {
                return <MenuItem value={item.value}>{item.label}</MenuItem>;
              })}
            </TextField>
          </Grid>

          {/* Vigências */}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              Vigência inicial
            </Typography>

            <CalendarDateTime
              title="Vigência inicial"
              value={formik.values.startAt}
              disabled={disabled}
              onChange={(iso) => formik.setFieldValue(START_AT, iso || "")}
              errorText={error(START_AT) as string | undefined}
              placeholder={
                isCreate
                  ? "Clique para abrir o calendário de de Vigência inicial"
                  : undefined
              }
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              Vigência final
            </Typography>

            <CalendarDateTime
              title="Vigência final"
              value={formik.values.expireAt}
              disabled={disabled}
              onChange={(iso) => formik.setFieldValue(EXPIRE_AT, iso || "")}
              errorText={error(EXPIRE_AT) as string}
              placeholder={
                isCreate
                  ? "Clique para abrir o calendário de Vigência final"
                  : undefined
              }
              minDate={new Date(formik.values.startAt)}
              sx={textFieldSx}
            />
          </Grid>
        </Grid>

        {/* RODAPÉ */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            sx={{ textTransform: "none", color: "#D91F05" }}
            onClick={onCancelOrClose}
            size={"large"}
          >
            Cancelar
          </Button>
          {showPreview && (
            <Button
              sx={{ textTransform: "none", color: "#D91F05" }}
              onClick={() => onPreview(formik.values)}
              variant="outlined"
              disabled={loading}
              size={"large"}
            >
              Pré-visualizar
            </Button>
          )}

          {showSubmit && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                textTransform: "none",
                bgcolor: !loading && formik.isValid ? "#D91F05" : "#F7C9C0",
                "&:hover": {
                  bgcolor: !loading && formik.isValid ? "#bf1a04" : "#F7C9C0",
                },
                derRadius: 1,
                minWidth: 120,
                color: "#FFFFFF",
              }}
              onClick={() => formik.handleSubmit()}
              size={"large"}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#FFF" }} />
              ) : (
                submitLabel
              )}
            </Button>
          )}
        </Box>

        {/* RESUMO DE ERROS APÓS TENTAR SALVAR */}
        {formik.submitCount > 0 && !formik.isValid && (
          <Box mt={2}>
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
              <AlertTitle>Revise os campos antes de salvar</AlertTitle>

              <Typography sx={{ fontSize: 13, mb: 1.5 }}>
                Para salvar a notificação, verifique:
              </Typography>

              <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: 13 }}>
                {buildErrorMessages(formik).map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NotificationForm;
